import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { Icon, message } from "antd";
import { CONSTITUTION_TYPES } from "constants/enums";
import CompanyLosService from "../../../modules/companies/services/company-los-services";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import {
    fetchCompanyProfile,
    fetchCaseDocuments,
    fetchCompanyDocuments,
    fetchCaseAssociates,
    fetchCaseSupplyChainDetails,
    fetchCaseSupplyChainFinancials,
    fetchStatement,
    fetchCamEngineView,
    fetchPortfolioView,
    addCaseAssociates,
    updateCaseDetailFields,
    fetchCaseDetailFields,
    fetchedCaseTvr,
    setGroupInfoAllValidated,
    // Bank statement analyser wiring (Jobs List + Manual/Upload actions)
    createStatement,
    updateStatement,
    deleteStatement,
    updateCaseDetail,
    fetchCaseDetail,
    fetchedCaseDetail,
    updateCaseRemarks,
    fetchCaseRemarks,
    createRequest,
    createProfiosRequest,
    fetchJobsList,
    fetchJobsDetails,
    fetchJobsDetailsPerfios,
    fetchStatementAnalysis,
    fetchStatementAnalysisPerfois,
    retryAnalysis,
    retryAnalysisPerfios,
    // Bank Accounts card (add / edit / key-account / applicability)
    updateCompanyBankAccounts,
    fetchCaseBankAccounts,
    addCaseBankAccounts
} from "store/actions";
import { CaseService, CompanyService } from "services/index";
import {
    accountSummaryList,
    generateCreditBrief,
    fetchBureauLoanDetails,
    fetchCaseDataComparison,
    fetchManufacturerGstPull
} from "services/case-service";
import { STATE_STATUS } from "constants";
import CreditCheckerNav from "./credit-checker-nav";
import CaseSummarySection from "./case-summary-section";
import CreditBriefSection from "./credit-brief-section";
import CreditDecisionSection from "./credit-decision-section";
import ActionableSection from "./actionable-section";
import PricingSection from "./pricing-section";
import CompanyScreeningCard from "./company-screening-card";
import {
    parseApplicantHits,
    parseCompanyScreening
} from "./trackwizz-utils";
import UpdatedTvrSection from "./updated-tvr-section";
import RemarksEditor from "../remarks-editor";
import CamAssessmentSection from "./cam-assessment-section";
import ExistingPortfolioSection from "./existing-portfolio-section";
import CompanyDetailSection from "./company-detail-section";
import ApplicantDetailsSection from "./applicant-details-section";
import ProductDetailsSection, {
    hasProductDetails
} from "./product-details-section";
import BureauLoanSection from "./bureau-loan-section";
// Shown as a collapsible accordion inside Current Loan Obligation for KSF|HHPL/
// HHPL Term Loan cases. Reuses the SAME component the Credit Appraisal tab uses.
import EligibilityCalculator from "../eligibility-calculator";
import TriangulationSection from "./triangulation-section";
import FinancialAnalysisTab from "../financial-analysis-tab";
import SectionBlock from "./components/section-block";
import CardBlock from "./components/card-block";
import BrandMixCard from "./components/brand-mix-card";
import PortfolioCreditRemarksDrawer from "../portfolio-credit-remarks-drawer";
// import RecommendationSection from "./recommendation-section"; // disabled for now
import {
    CREDIT_CHECKER_SECTIONS,
    SECTION_IDS,
    LIMIT_VALIDITY_MONTHS,
    LIMIT_EXPIRING_SOON_DAYS,
    INACTIVE_CASE_STATUS_HINTS
} from "./constants";
import { getCreditCheckerMock } from "./mock-data";
import {
    buildApplicants,
    buildCallContacts,
    buildCompany,
    buildTriangulationDocs,
    buildTopMetrics,
    buildPricing
    // buildRecommendation // Recommendation section disabled for now
} from "./data-utils";
import { buildCamAssessment } from "./cam-utils";
import {
    buildTriangulation,
    buildTriangulationFromApi
} from "./triangulation-builder";
import { buildBureauLoans, buildBureauSummaries } from "./bureau-loan-builder";
import "./../../../styles/credit-checker.css";

// Rows per page for the server-paginated Brand Mix (manufacturer GST pull) table.
// Pan-wise grouping yields few rows, so keep the page small enough that the
// pagination control actually surfaces.
const BRAND_MIX_PAGE_SIZE = 5;

// Derives a limit-expiry date + status for an All-Cases row. The account summary
// carries no explicit expiry, so expiry = sanction_date + LIMIT_VALIDITY_MONTHS.
// A case is flagged "expiring" within LIMIT_EXPIRING_SOON_DAYS of that date and
// "expired" once past it — but only while the limit is still live (inactive
// statuses like Closed/Rejected are never flagged, their expiry being moot).
function deriveLimitExpiry(sanctionDateRaw, caseStatus) {
    if (!sanctionDateRaw) {
        return { expiry_date: null, expiry_status: null };
    }
    const start = moment(String(sanctionDateRaw).slice(0, 10));
    if (!start.isValid()) {
        return { expiry_date: null, expiry_status: null };
    }
    const expiry = start.clone().add(LIMIT_VALIDITY_MONTHS, "months");
    const expiryLabel = expiry.format("DD/MM/YYYY");
    const status = String(caseStatus || "").toLowerCase();
    const inactive = INACTIVE_CASE_STATUS_HINTS.some(
        hint => status.indexOf(hint) !== -1
    );
    if (inactive) {
        return { expiry_date: expiryLabel, expiry_status: null };
    }
    const daysLeft = expiry.diff(moment(), "days");
    let expiryStatus = "ok";
    if (daysLeft < 0) {
        expiryStatus = "expired";
    } else if (daysLeft <= LIMIT_EXPIRING_SOON_DAYS) {
        expiryStatus = "expiring";
    }
    return { expiry_date: expiryLabel, expiry_status: expiryStatus };
}

/**
 * Credit Checker tab.
 *
 * The content column is its own scroll container, so the left icon-rail stays
 * pinned regardless of any ancestor `overflow` (a plain sticky-on-the-page
 * approach was being clipped by a parent). Scroll-spy is rooted to that same
 * container.
 *
 * Sections:
 *   1. Case Summary      — verification call notes + recording + transcript
 *   2. Applicant Details — KYC docs (photo/PAN/Aadhaar) + bureau per applicant
 *   3. GST / Ledger / Banking triangulation
 *
 * Applicant identity, KYC documents and bureau scores use real data; the call
 * notes, recording, transcript and triangulation fall back to per-case mocks
 * (mock-data.js) until those backend APIs ship — currently wired for 158262.
 */
class CreditChecker extends React.Component {
    constructor(props) {
        super(props);
        this.contentRef = React.createRef();
        this.sectionRefs = CREDIT_CHECKER_SECTIONS.reduce((acc, section) => {
            acc[section.id] = React.createRef();
            return acc;
        }, {});
        this.intersectionRatios = {};
        this.observer = null;
        this.state = {
            activeSection: SECTION_IDS.CASE_SUMMARY,
            cibilDocs: null,
            cibilDocsLoading: true,
            bureauRecords: null,
            repaymentData: null,
            limitInfo: null,
            limitLoading: true,
            allCases: null,
            casesLoading: true,
            // While true, heavy inline embeds (the recommendation PDF viewer)
            // render a lightweight placeholder so the print preview is fast.
            printing: false,
            // Case-associate id whose bureau remark is currently being saved.
            savingRemarkId: null,
            // Case-associate id whose "Is Main Borrower" toggle is mid-save.
            savingMainBorrowerId: null,
            // Company-associate id whose Relation picker is mid-save.
            savingRelationshipId: null,
            // Company-associate id whose Shareholding is mid-save.
            savingShareholdingId: null,
            // Company-associate id whose Email is mid-save.
            savingEmailId: null,
            // Eligibility Calculator prefill data (Term Loan cases only).
            calculatorData: {},
            // Applicant (company_associate) id whose CIBIL re-pull is in flight.
            triggeringCibilId: null,
            // True while the CAM (Credit Assessment) refresh is in flight.
            camRefreshing: false,
            // True while the Total Exposure card's server-side rebuild
            // (build_exposure_data=true) is in flight.
            exposureRefreshing: false,
            // True while a CAM update (save edited credit remarks) is in flight.
            camUpdating: false,
            // Draft credit-remark edits keyed by CAM rule id (key2), applied on
            // Update.
            camRemarkEdits: {},
            // Credit Brief PDF: server-generated URL + loading flag.
            creditBriefLoading: false,
            creditBriefUrl: null,
            // Credit Summary PDF: object-URL to a blob rendered on demand
            // by /credit-summary/ (no S3 store; released on unmount).
            creditSummaryLoading: false,
            creditSummaryUrl: null,
            // Bumped after the TVR submit re-runs the AI decision, so the
            // Credit Decision card reloads the fresh result.
            aiReloadKey: 0,
            // Per-applicant active loans from the latest bureau pull.
            bureauLoans: null,
            bureauLoansLoading: true,
            // Per-applicant combined Loans + Enquiries by-type summary rows,
            // keyed by applicant id (mirrors the Credit Brief PDF's table).
            bureauSummary: {},
            // Bureau loan filters (server-side): which loans to include and
            // whether closed accounts are shown. Default: non-Progfin, open only.
            bureauLoanFilter: "non_progfin_loans",
            bureauIncludeClosed: false,
            // Latest portfolio-instance remark text per row key (Portfolio View
            // "Comments" column), fetched once portfolioView resolves.
            portfolioComments: {},
            // Portfolio remarks drawer (add/read comments on a Portfolio View row).
            portfolioRemarksDrawerVisible: false,
            portfolioRemarksRecord: null,
            // Real GST × Banking × Ledger triangulation from the case
            // data-comparison API (replaces the per-source mock fallback).
            dataComparison: null,
            dataComparisonLoading: true,
            // Brand Mix (manufacturer GST pull, pan-wise) — server-paginated.
            brandMix: null,
            brandMixLoading: true,
            brandMixCount: 0,
            brandMixPage: 1,
            brandMixSearch: "",
            // Trackwizz screening (AML/PEP/sanctions). Fetched once from
            // CompanyService.trackwizzEntity; parsed lazily by
            // parseApplicantHits + parseCompanyScreening in render.
            trackwizzData: null,
            trackwizzLoading: false,
            sections: CREDIT_CHECKER_SECTIONS.reduce((acc, section) => {
                // Sections marked `defaultCollapsed: true` (e.g. inline
                // Financial Analysis) start collapsed; everything else opens
                // expanded so the reviewer's default view is comprehensive.
                acc[section.id] = !section.defaultCollapsed;
                return acc;
            }, {})
        };
    }

    toggleSection = id =>
        this.setState(prev => ({
            sections: { ...prev.sections, [id]: !prev.sections[id] }
        }));

    setAllSections = expanded =>
        this.setState({
            sections: CREDIT_CHECKER_SECTIONS.reduce((acc, section) => {
                acc[section.id] = expanded;
                return acc;
            }, {})
        });

    // Limit Assessment tracker — Sales Proposed Limit and Proposed Limit are
    // both written through the same audited generic endpoint
    // (case/<id>/limit/, limit_type-discriminated), replacing the old
    // per-field endpoints (updateCaseDetailFields for Proposed Limit, the
    // legacy sales-proposed-limit/ endpoint for Sales Proposed Limit). The
    // promise is left to reject with the raw error so the tracker modal can
    // show field-level messages (remark / value / non_field_errors); we still
    // refetch the case detail on success so the Limit Assessment tile
    // rebuilds from the saved value.
    onLimitChange = (limitType, value, remark) =>
        CaseService.updateLimit(this.props.caseId, {
            limit_type: limitType,
            value,
            remark
        }).then(res => {
            message.success("Limit updated");
            if (this.props.fetchCaseDetail) {
                this.props.fetchCaseDetail(this.props.caseId);
            }
            return res;
        });

    onProposedLimitChange = (value, remark) =>
        this.onLimitChange("PROPOSED_LIMIT", value, remark);

    onSalesProposedLimitChange = (value, remark) =>
        this.onLimitChange("SALES_PROPOSED_LIMIT", value, remark);

    // TVR submit already re-ran the AI agent (refresh=true); bump the key so the
    // Credit Decision card reloads the freshly-stored result.
    handleAiRefreshed = () =>
        this.setState(prev => ({ aiReloadKey: prev.aiReloadKey + 1 }));

    // Total Exposure card (Limit Assessment) — forces the backend to recompute
    // exposure_data (build_exposure_data=true) rather than reading the cached
    // value, then pushes the fresh case record into redux the same way a normal
    // case-detail refetch does, so every consumer of caseDetail sees it too.
    handleRefreshExposureData = () => {
        if (this.state.exposureRefreshing) {
            return;
        }
        this.setState({ exposureRefreshing: true });
        CaseService.rebuildCaseExposureData(this.props.caseId)
            .then(({ data }) => {
                if (this.props.fetchedCaseDetail) {
                    this.props.fetchedCaseDetail(this.props.caseId, data);
                }
            })
            .catch(() => {
                message.error("Could not refresh exposure data");
            })
            .finally(() => {
                this.setState({ exposureRefreshing: false });
            });
    };

    // Case Summary (remarks) — same handlers as the Credit Appraisal tab.
    getCaseRemarks = () => this.props.fetchCaseRemarks(this.props.caseId);

    onRemarksEdit = (value, isManuallySaved) => {
        this.props.updateCaseRemarks(
            this.props.caseId,
            value,
            this.props.caseInternalRemarks,
            isManuallySaved
        );
    };

    componentDidMount() {
        this.ensureData();
        this.fetchCibilDocs();
        this.fetchBureauRecords();
        this.fetchRepaymentData();
        this.fetchExistingPortfolio();
        this.fetchBureauLoans();
        this.fetchDataComparison();
        this.fetchBrandMix();
        this.fetchTrackwizz();
        this.fetchEligibilityCalculatorData();
        // Portfolio View (aggregated performance by dimension) — read-only GET
        // via redux (companyLosProfiles.portfolioViewData), keyed by case id.
        if (this.props.caseId) {
            this.props.fetchPortfolioView(this.props.caseId);
        }
        this.setupScrollSpy();
        // Auto-load the Credit Brief PDF so the section shows it by default (the
        // last section) — uses the stored S3 copy, generating once if missing.
        if (this.props.caseId) {
            this.loadCreditBrief(false);
        }
        // Reset the print placeholder once the dialog closes (covers a direct
        // Cmd/Ctrl+P too).
        window.addEventListener("afterprint", this.handleAfterPrint);
    }

    handleAfterPrint = () => {
        if (this.state.printing) {
            this.setState({ printing: false });
        }
    };

    // Prefill data for the Eligibility Calculator (Term Loan cases). Same GET the
    // Credit Appraisal tab uses; result only consumed when the calculator renders.
    fetchEligibilityCalculatorData() {
        if (!this.props.caseId) {
            return;
        }
        CaseService.getEligibilityCalculatorData(this.props.caseId)
            .then(res => {
                this.setState({
                    calculatorData: (res && res.status === 200 && res.data) || {}
                });
            })
            .catch(() => this.setState({ calculatorData: {} }));
    }

    // Existing Portfolio: Limit Info (buyer exposure) + All Cases detail
    // (per-case account summary, joined with case status/FI partner). All
    // read-only GETs, same sources as the Case Details / All Cases tabs.
    // Limit Info (buyer exposure). `forceBuild` re-derives it server-side
    // (`?force_build=true`) — used by the Rebuild button; the plain load reads the
    // cached view. A successful rebuild also forces the Total Exposure card to
    // recompute (it's derived from these same buyer-exposure figures), so it
    // doesn't keep showing a stale total.
    fetchLimitInfo = (forceBuild = false) => {
        this.setState({ limitLoading: true });
        return CompanyService.getCaseBuyerExposure(this.props.caseId, forceBuild)
            .then(res => {
                this.setState({
                    limitInfo: (res && res.data && res.data.data) || [],
                    limitLoading: false
                });
                if (forceBuild) {
                    message.success("Limit info rebuilt");
                    this.handleRefreshExposureData();
                }
            })
            .catch(() => {
                this.setState({ limitInfo: [], limitLoading: false });
                if (forceBuild) {
                    message.error("Failed to rebuild limit info");
                }
            });
    };

    handleRebuildLimitInfo = () => this.fetchLimitInfo(true);

    fetchExistingPortfolio() {
        this.fetchLimitInfo();

        CaseService.newCaseSearchList(this.props.caseId)
            .then(res => {
                // Show every case for this borrower in the "All Cases" grid,
                // including the one currently open on this page.
                const cases = (res && res.data) || [];
                const ids = cases.map(c => c.id);
                if (!ids.length) {
                    this.setState({ allCases: [], casesLoading: false });
                    return;
                }
                accountSummaryList([ids])
                    .then(summaryRes => {
                        const results =
                            (summaryRes &&
                                summaryRes.data &&
                                summaryRes.data.results) ||
                            [];
                        const summaryById = {};
                        results.forEach(r => {
                            summaryById[r.case] = r;
                        });
                        this.setState({
                            allCases: this.buildPortfolioCases(
                                cases,
                                summaryById
                            ),
                            casesLoading: false
                        });
                    })
                    .catch(() =>
                        // Still show the cases (status/FI) even if metrics fail.
                        this.setState({
                            allCases: this.buildPortfolioCases(cases, {}),
                            casesLoading: false
                        })
                    );
            })
            .catch(() => this.setState({ allCases: [], casesLoading: false }));
    }

    // Builds the detailed All-Cases rows from the case list (authoritative
    // status + FI), enriched with each case's account-summary metrics (by id).
    // Driving off the case list keeps the status correct for every case.
    buildPortfolioCases(cases, summaryById) {
        return (cases || []).map(c => {
            const s = summaryById[c.id] || {};
            // ISO date (YYYY-MM-DD…) → DD/MM/YYYY, matching the grid's date cells.
            const toDisplayDate = raw =>
                raw
                    ? String(raw)
                        .slice(0, 10)
                        .split("-")
                        .reverse()
                        .join("/")
                    : null;
            // Prefer the previous-case payload (same source the All Cases tab
            // reads), fall back to the account-summary API when absent.
            const rawSanctionDate =
                c.sanction_date != null ? c.sanction_date : s.sanction_date;
            const sanctionDate = toDisplayDate(rawSanctionDate);
            const expiry = deriveLimitExpiry(
                rawSanctionDate,
                c.case_status_display
            );
            return {
                caseId: `PGCP-${c.id}`,
                // Same fields (and same *_display keys) the All Cases tab shows,
                // straight off the case-list payload.
                parent_case_id: c.parent_case_detail,
                case_type: c.case_type_display,
                // Product + last disbursal come off the case list (call #1), not
                // the account summary. "Undefined" (older cases) reads as no value.
                product_type:
                    c.business_product_type_display &&
                    c.business_product_type_display !== "Undefined"
                        ? c.business_product_type_display
                        : null,
                last_disbursal_date: toDisplayDate(c.last_disbursal_date),
                case_status: c.case_status_display,
                current_stage: c.current_stage_display,
                fi_partner: (c.bank && (c.bank.code || c.bank.name)) || null,
                id: c.id,
                bank: c.bank,
                can_be_tagged_as_fi_movement: c.can_be_tagged_as_fi_movement,
                can_be_parent_of_fi_movement: c.can_be_parent_of_fi_movement,
                can_be_untagged_as_fi_movement:
                    c.can_be_untagged_as_fi_movement,
                // These fields exist on BOTH the previous-case payload (`c`) and
                // the account-summary API (`s`). Read `c` FIRST so the grid
                // matches the All Cases tab (same case-list source); the
                // account-summary value is only a fallback when `c` is missing it.
                sellers: c.seller != null ? c.seller : s.sellers,
                anchors: c.anchor != null ? c.anchor : s.anchors,
                sanction_date: sanctionDate,
                expiry_date: expiry.expiry_date,
                expiry_status: expiry.expiry_status,
                // Sanctioned Limit is taken from the previous-case payload (`c`)
                // FIRST so it matches the All Cases tab, which reads the same
                // case-list field. The account-summary value (`s.sanction_limit`,
                // the LMS-side number) is only a fallback and could otherwise
                // diverge from All Cases.
                sanction_limit:
                    c.sanction_limit != null
                        ? c.sanction_limit
                        : s.sanction_limit,
                // Same combined "Proposed / Sanctioned Limit" field the All Cases
                // tab's main table shows (proposed until sanctioned, then the
                // sanctioned value).
                proposed_or_sanction_limit: c.proposed_or_sanction_limit,
                scheme_type: c.scheme_type,
                fi_movement: c.fi_movement_display,
                renewal: c.renewal_display,
                closure_type: c.closure_type,
                is_bundled: c.is_bundled,
                is_rotation_case: c.is_rotation_case,
                is_balance_transfer_case: c.is_balance_transfer_case,
                adhoc_limit:
                    c.adhoc_limit != null ? c.adhoc_limit : s.adhoc_limit,
                loaded_limit: s.loaded_limit,
                avg_utilization: s.avg_utilization,
                repayment_amount: s.repayment_amount,
                throughput: s.throughput_last_12_month,
                max_dpd: s.max_dpd,
                dpd_6m: s.dpd_last_6_months,
                tenure: s.tenure,
                emi_paid: s.emi_paid,
                emi_bounce: s.emi_bounce,
                first_disbursal_ageing: s.first_disbursal_ageing,
                max_ageing: s.max_ageing,
                max_ageing_12m: s.max_ageing_in_last_12_months,
                perc_od: s.perc_invoices_gone_in_od,
                invoices_12m: s.invoices_funded_in_last_12_months,
                od_30: s.overdue_less_than_30_days,
                od_30_60: s.overdue_between_30_to_60_days,
                od_60_90: s.overdue_between_60_to_90_days,
                od_90: s.overdue_greater_than_90_days
            };
        });
    }

    // Real ledger-repayment month-on-month data (same source as the Credit
    // Appraisal "Total Repayments" table). Read-only GET.
    fetchRepaymentData() {
        CompanyService.getTotalRepaymentDetails(this.props.caseId)
            .then(res => {
                this.setState({
                    repaymentData:
                        (res && res.data) || []
                });
            })
            .catch(() => this.setState({ repaymentData: [] }));
    }

    // External loan obligations per applicant from the latest bureau pull
    // (closed accounts + guarantor obligations filtered server-side). Read-only
    // GET — the section renders an empty state when the API returns no records.
    fetchBureauLoans() {
        // Capture the params this request is actually sent with — the filter can
        // change while it is in flight, and the response must be read back under
        // the key it was requested for, not whatever state holds on arrival.
        const filterKey = this.state.bureauLoanFilter || "non_progfin_loans";
        const includeClosed = this.state.bureauIncludeClosed;
        // Every add / edit / obligation-toggle triggers a refresh, so several
        // GETs can be in flight at once and resolve out of order. Stamp each one
        // and let only the newest write state, else an older response clobbers
        // the fresh data and the row appears un-updated.
        const seq = (this._bureauLoansSeq || 0) + 1;
        this._bureauLoansSeq = seq;
        const isStale = () => seq !== this._bureauLoansSeq;

        this.setState({ bureauLoansLoading: true });
        fetchBureauLoanDetails(this.props.caseId, {
            loanFilter: filterKey,
            includeClosed
        })
            .then(res => {
                if (isStale()) {
                    return;
                }
                // V2 (bureau-overview) returns { bureau_loan_detail: [ applicant ] }.
                // Each applicant carries its loans under a key named by the active
                // loan_filter (all_loans / progfin_loans / non_progfin_loans) rather
                // than a fixed `loans` key — normalize it to `loans` for the builder.
                const body = (res && res.data) || {};
                const detail = body.bureau_loan_detail || body.data || [];
                const raw = (Array.isArray(detail) ? detail : []).map(group => ({
                    ...group,
                    loans:
                        group.loans ||
                        group[filterKey] ||
                        group.all_loans ||
                        group.non_progfin_loans ||
                        group.progfin_loans ||
                        []
                }));
                // Combined Loans + Enquiries by-type summary (second table),
                // sourced from the same response's summary sections.
                const bureauSummary = buildBureauSummaries(
                    body.bureau_loan_summary || [],
                    body.bureau_enquiry_summary || [],
                    filterKey
                );
                this.setState({
                    bureauLoans: buildBureauLoans(raw),
                    bureauSummary,
                    bureauLoansLoading: false
                });
            })
            .catch(() => {
                if (isStale()) {
                    return;
                }
                this.setState({
                    bureauLoans: null,
                    bureauSummary: {},
                    bureauLoansLoading: false
                });
            });
    }

    // Re-pull consumer CIBIL for one applicant (company_associate id) — the same
    // endpoint the Case Details tab's Consumer "Trigger" fires. On success the
    // bureau loans (and bureau records) are refetched so the section shows the
    // fresh pull date and any new obligations.
    //
    // The endpoint enforces a per-applicant pull limit (one fresh pull a month,
    // plus one within-month retry when the last score was bad). When the limit is
    // hit it still returns HTTP 200 but with `cibil_retry_limit_reached: true`
    // (status "retry_limit_reached") and NO fresh pull — branch on that flag, not
    // on the HTTP status, and don't refetch as if a new pull happened.
    handleTriggerCibil = applicantId => {
        const { caseId } = this.props;
        if (!caseId || !applicantId) {
            return;
        }
        this.setState({ triggeringCibilId: applicantId });
        CaseService.triggerCibilConsumer(caseId, applicantId)
            .then(({ data }) => {
                if (
                    data &&
                    (data.cibil_retry_limit_reached ||
                        data.status === "retry_limit_reached")
                ) {
                    message.warning(
                        data.message ||
                            "CIBIL retry limit reached for this month."
                    );
                    return;
                }
                if (data && data.message === "success") {
                    message.success("CIBIL triggered successfully");
                    this.fetchBureauLoans();
                    this.fetchBureauRecords();
                } else {
                    message.error("Something went wrong");
                }
            })
            .catch(err =>
                message.error((err && err[0]) || "Something went wrong")
            )
            .finally(() => this.setState({ triggeringCibilId: null }));
    };

    // Brand mix — GST-pull manufacturers/suppliers (pan-wise), server-paginated
    // and server-searched (the `search` query param filters across all pages,
    // so it must not be applied client-side to the current page only).
    fetchBrandMix(
        page = this.state.brandMixPage || 1,
        search = this.state.brandMixSearch || ""
    ) {
        if (!this.props.caseId) {
            return;
        }
        this.setState({ brandMixLoading: true });
        const params = { page, page_size: BRAND_MIX_PAGE_SIZE };
        if (search) {
            params.search = search;
        }
        fetchManufacturerGstPull(this.props.caseId, params)
            .then(res => {
                const body = (res && res.data) || {};
                const rows = Array.isArray(body) ? body : body.results || [];
                const count =
                    body.count !== null && body.count !== undefined
                        ? body.count
                        : rows.length;
                this.setState({
                    brandMix: rows,
                    brandMixCount: count,
                    brandMixPage: page,
                    brandMixSearch: search,
                    brandMixLoading: false
                });
            })
            .catch(() =>
                this.setState({
                    brandMix: [],
                    brandMixCount: 0,
                    brandMixLoading: false
                })
            );
    }

    // Page change from the Brand Mix table's pagination control.
    setBrandMixPage = page => this.fetchBrandMix(page);

    // Search from the Brand Mix header. Always refetches from page 1 — staying
    // on page N of the old result set would ask the server for a page the
    // filtered set may not have, and land on an empty table.
    setBrandMixSearch = search => this.fetchBrandMix(1, (search || "").trim());

    // Trackwizz screening — AML/PEP/sanctions hit summary for the company and
    // each applicant. Fed to the KYC row's Probable/Confirmed columns (via
    // `applicantHits`) and to the Company Screening card in Company Detail.
    fetchTrackwizz = () => {
        const { companyId } = this.props;
        if (!companyId) return;
        this.setState({ trackwizzLoading: true });
        CompanyService.trackwizzEntity(companyId)
            .then(res => {
                this.setState({
                    trackwizzData: (res && res.data) || null,
                    trackwizzLoading: false
                });
            })
            .catch(() => {
                this.setState({ trackwizzData: null, trackwizzLoading: false });
            });
    };

    // Filter changes re-fetch from the server (the API applies the filtering).
    setBureauLoanFilter = value =>
        this.setState({ bureauLoanFilter: value }, () => this.fetchBureauLoans());

    setBureauIncludeClosed = checked =>
        this.setState({ bureauIncludeClosed: checked }, () =>
            this.fetchBureauLoans()
        );

    // Real GST × Banking × Ledger triangulation. Read-only GET — the section
    // falls back to the per-case mock baseline (only populated for 158262)
    // when the API returns no overlapping months.
    fetchDataComparison() {
        fetchCaseDataComparison(this.props.caseId)
            .then(res => {
                this.setState({
                    dataComparison: (res && res.data) || null,
                    dataComparisonLoading: false
                });
            })
            .catch(() =>
                this.setState({
                    dataComparison: null,
                    dataComparisonLoading: false
                })
            );
    }

    componentDidUpdate(prevProps) {
        // The key bank account id only resolves once the company profile loads,
        // so request its statements lazily (once).
        if (this.props.activeBankAccountKey && !this._statementRequested) {
            this._statementRequested = true;
            this.props.fetchStatement(
                this.props.caseId,
                this.props.activeBankAccountKey
            );
        }
        // A bureau-remark save refetches caseAssociates on success — clear the
        // per-row saving state once the records come back.
        if (
            this.state.savingRemarkId !== null &&
            prevProps.caseAssociates !== this.props.caseAssociates
        ) {
            this.setState({ savingRemarkId: null });
        }
        // Clear the main-borrower saving state once the refetch lands.
        if (
            this.state.savingMainBorrowerId !== null &&
            prevProps.caseAssociates !== this.props.caseAssociates
        ) {
            this.setState({ savingMainBorrowerId: null });
        }
        // Same for the Relation picker.
        if (
            this.state.savingRelationshipId !== null &&
            prevProps.caseAssociates !== this.props.caseAssociates
        ) {
            this.setState({ savingRelationshipId: null });
        }
        // Once the Portfolio View rows resolve, fetch the latest saved remark per
        // row so the "Comments" column can show it (once).
        if (
            !this._portfolioCommentsFetched &&
            this.props.portfolioView &&
            this.props.portfolioView.length
        ) {
            this._portfolioCommentsFetched = true;
            this.fetchPortfolioComments(this.props.portfolioView);
        }
    }

    // Fetch the latest saved remark text for each portfolio row that has an
    // instance id, keyed by the row `key` (= remark_type). Best-effort: a failed
    // row simply shows no comment.
    fetchPortfolioComments = rows => {
        const targets = (rows || []).filter(
            r => r && r.portfolioInstanceId != null && r.key
        );
        if (!targets.length) {
            return;
        }
        Promise.all(
            targets.map(r =>
                CompanyLosService.portfolioInstanceRemarks(
                    this.props.caseId,
                    r.portfolioInstanceId,
                    r.key
                )
                    .then(({ data }) => {
                        const arr = Array.isArray(data) ? data : [];
                        const latest = arr
                            .slice()
                            .sort(
                                (a, b) =>
                                    new Date(b.create_date || 0) -
                                    new Date(a.create_date || 0)
                            )[0];
                        return { key: r.key, text: (latest && latest.remark) || "" };
                    })
                    .catch(() => ({ key: r.key, text: "" }))
            )
        ).then(results => {
            const map = {};
            results.forEach(x => {
                if (x && x.key) {
                    map[x.key] = x.text;
                }
            });
            this.setState({ portfolioComments: map });
        });
    };

    // Portfolio View rows decorated with the latest remark text (Comments column).
    getPortfolioViewWithComments = () => {
        const rows = this.props.portfolioView || [];
        const map = this.state.portfolioComments || {};
        const enriched = rows.map(r => ({
            ...r,
            comments: (r && r.key && map[r.key]) || ""
        }));
        // Credit-team ordering: Anchor first, then Seller, then Geo / Industry
        // / RBH. The API emits Seller first — reorder client-side to keep the
        // view stable regardless of backend order.
        const rank = { anchor: 0, seller: 1, geo: 2, industry: 3, rbh: 4 };
        return enriched.slice().sort((a, b) => {
            const ra = rank[a && a.key];
            const rb = rank[b && b.key];
            if (ra === undefined && rb === undefined) return 0;
            if (ra === undefined) return 1;
            if (rb === undefined) return -1;
            return ra - rb;
        });
    };

    // Portfolio remarks drawer — the same component (and endpoints) the Case
    // Summary tab uses, so a comment added here is the same thread as there.
    openPortfolioRemarksDrawer = record =>
        this.setState({
            portfolioRemarksDrawerVisible: true,
            portfolioRemarksRecord: record
        });

    closePortfolioRemarksDrawer = () =>
        this.setState({
            portfolioRemarksDrawerVisible: false,
            portfolioRemarksRecord: null
        });

    // After a save, re-pull the remark text so the Comments column reflects it
    // without a full page refresh.
    handlePortfolioRemarkSaved = () => {
        this.fetchPortfolioComments(this.props.portfolioView);
    };

    // Persist an applicant's bureau remark, mirroring the Credit Appraisal CIBIL
    // save: merge the full case-associate record with the new remark and PUT it
    // via addCaseAssociates (saga handles the toast + refetch).
    handleSaveBureauRemark = (caseAssociateId, remark) => {
        const list = this.props.caseAssociates;
        const assoc =
            list &&
            list.find(a => Number(a.get("id")) === Number(caseAssociateId));
        if (!assoc) {
            return;
        }
        const base = assoc.toJS();
        const obj = {
            ...base,
            id: caseAssociateId,
            cibil_remarks: remark,
            applicable_bureau: base.applicable_bureau || base.bureau,
            cibil_report:
                base.cibil_report && base.cibil_report.id
                    ? base.cibil_report.id
                    : base.cibil_report,
            cibil_supported_report: Array.isArray(base.cibil_supported_report)
                ? base.cibil_supported_report.map(r => (r && r.id ? r.id : r))
                : base.cibil_supported_report
        };
        Object.keys(obj).forEach(k => {
            if (obj[k] === "") {
                obj[k] = null;
            }
        });
        this.setState({ savingRemarkId: caseAssociateId });
        this.props.addCaseAssociates(this.props.caseId, [obj], this.props.companyId);
    };

    // Update an applicant's Relationship with Borrower via the existing
    // PUT /case/add-remove-associates/ (addCaseAssociates). That endpoint is an
    // upsert — associates missing from the payload are NOT removed (removal is a
    // separate DELETE) — so a single-associate body is safe. The backend writes
    // the value to BOTH CaseCompanyAssociate and its CompanyAssociate, and the
    // saga refetches case associates + the company profile on success.
    //
    // `id` here must be the COMPANY associate id (the endpoint resolves it via
    // PrimaryKeyRelatedField(queryset=CompanyAssociate)), which is what
    // buildApplicants exposes as `associateId`.
    handleChangeRelationship = (companyAssociateId, value) => {
        if (!companyAssociateId || !this.props.caseId) {
            return;
        }
        this.setState({ savingRelationshipId: companyAssociateId });
        this.props.addCaseAssociates(
            this.props.caseId,
            [
                {
                    id: companyAssociateId,
                    relationship_with_borrower: value
                }
            ],
            this.props.companyId
        );
    };

    // Update one applicant's shareholding %. Only offered for a Partnership,
    // where the stake is split across partners (see isPartnership). PATCHes the
    // company-associate directly, then refetches the associates slice so the
    // table re-renders from the server value rather than an optimistic guess.
    handleUpdateShareholding = (companyAssociateId, value) => {
        const { caseId, companyId } = this.props;
        if (!companyAssociateId || !companyId) {
            return;
        }
        this.setState({ savingShareholdingId: companyAssociateId });
        CompanyService.updateAssociatesCibil(companyAssociateId, {
            shareholding: value
        })
            .then(() => {
                message.success("Shareholding updated");
                // buildApplicants reads `shareholding` off company.associates —
                // the COMPANY PROFILE slice, not caseAssociates — so the profile
                // is what has to be refetched or the cell keeps the stale value.
                // Refresh both, since the table derives from the two together.
                this.props.fetchCompanyProfile(companyId, caseId);
                this.props.fetchCaseAssociates(caseId);
            })
            .catch(() => {
                message.error("Failed to update shareholding");
            })
            .finally(() => {
                this.setState({ savingShareholdingId: null });
            });
    };

    // Update one applicant's email. Same PATCH-then-refetch shape as
    // handleUpdateShareholding: buildApplicants reads `email` off
    // company.associates, so the company profile has to be refetched or the
    // cell keeps the stale value.
    handleUpdateEmail = (companyAssociateId, value) => {
        const { caseId, companyId } = this.props;
        if (!companyAssociateId || !companyId) {
            return;
        }
        this.setState({ savingEmailId: companyAssociateId });
        CompanyService.updateAssociatesCibil(companyAssociateId, {
            email: value
        })
            .then(() => {
                message.success("Email updated");
                this.props.fetchCompanyProfile(companyId, caseId);
                this.props.fetchCaseAssociates(caseId);
            })
            .catch(() => {
                message.error("Failed to update email");
            })
            .finally(() => {
                this.setState({ savingEmailId: null });
            });
    };

    // Compare the company's constitution against a CONSTITUTION_TYPES key.
    // `company` may be an Immutable Map or a plain object depending on the slice.
    isConstitution = (name) => {
        const company = this.props.company;
        if (!company) {
            return false;
        }
        const constitution =
            typeof company.get === "function"
                ? company.get("constitution")
                : company.constitution;
        return Number(constitution) === Number(CONSTITUTION_TYPES.get(name));
    };

    // True when the company is a Partnership. Drives the editable shareholding
    // column, where the stake is split across partners.
    isPartnership = () => this.isConstitution("Partnership");

    // Toggle an applicant's "Is Main Borrower" flag. Mirrors the Case Summary
    // applicants modal: PUT the FULL company_associates list (slim shape —
    // { id, type, is_authorized_signatory, is_main_borrower }), flipping just the
    // toggled associate, via addCaseAssociates → { case, company_associates }.
    //
    // ANY number of applicants can be a main borrower, whatever the company's
    // constitution — same as Case Summary, which never clears the other rows.
    // Do NOT reintroduce a "turning one on clears the rest" rule here without
    // changing Case Summary too, or the two screens will disagree.
    handleToggleMainBorrower = (caseAssociateId, value) => {
        const list = this.props.caseAssociates;
        if (!list) {
            return;
        }
        const companyAssociates = list
            .toJS()
            .filter(
                a => a.company_associate && a.company_associate.id !== undefined
            )
            .map(a => {
                const isTarget = Number(a.id) === Number(caseAssociateId);
                return {
                    id: a.company_associate.id,
                    type: a.type,
                    is_authorized_signatory: a.is_authorized_signatory,
                    is_main_borrower: isTarget ? value : a.is_main_borrower
                };
            });
        this.setState({ savingMainBorrowerId: caseAssociateId });
        this.props.addCaseAssociates(
            this.props.caseId,
            companyAssociates,
            this.props.companyId
        );
    };

    // Re-run the CAM (Credit Assessment) engine — same call the CAM View's
    // Refresh button makes (POST cam-automation?refresh=true), then refetch the
    // CAM view + case fields so the section reflects the new result.
    handleRefreshCam = () => {
        if (!this.props.caseId || this.state.camRefreshing) {
            return;
        }
        this.setState({ camRefreshing: true });
        CaseService.refreshCamsData(this.props.caseId, {})
            .then(() => {
                this.setState({ camRefreshing: false });
                this.props.fetchCamEngineView(this.props.caseId);
                this.props.fetchCaseDetailFields(this.props.caseId);
            })
            .catch(() => {
                this.setState({ camRefreshing: false });
                message.error("Failed to refresh Credit Assessment");
            });
    };

    // Track an edited credit remark for a CAM parameter (keyed by rule id).
    onEditCamRemark = (key2, value) =>
        this.setState(prev => ({
            camRemarkEdits: { ...prev.camRemarkEdits, [key2]: value }
        }));

    // Persist edited credit remarks — same POST the CAM View's Update button
    // makes (update/cam-automation), sending the full cam-engine map with the
    // edited credit_remarks merged in, then refetch so the section reflects it.
    handleUpdateCam = () => {
        if (!this.props.caseId || this.state.camUpdating) {
            return;
        }
        const raw = this.props.camEngineData;
        const plain =
            raw && typeof raw.toJS === "function" ? raw.toJS() : raw || {};
        const edits = this.state.camRemarkEdits || {};
        const payload = {};
        Object.keys(plain).forEach(key => {
            const entry = plain[key] || {};
            payload[key] = {
                ...entry,
                key2: key,
                credit_remarks:
                    key in edits
                        ? edits[key]
                        : entry.credit_remarks || ""
            };
        });
        this.setState({ camUpdating: true });
        CaseService.updateCamsData(this.props.caseId, payload)
            .then(() => {
                this.setState({ camUpdating: false, camRemarkEdits: {} });
                message.success("Credit Assessment updated");
                this.props.fetchCamEngineView(this.props.caseId);
                this.props.fetchCaseDetailFields(this.props.caseId);
            })
            .catch(() => {
                this.setState({ camUpdating: false });
                message.error("Failed to update Credit Assessment");
            });
    };

    // Load the Credit Brief: the backend composes + renders it from the case id,
    // stores it on S3, and returns the public url which we embed/link to. Pass
    // regenerate=true (the "Regenerate" button) to force a fresh render.
    loadCreditBrief = (regenerate = false) => {
        if (this.state.creditBriefLoading) {
            return;
        }
        this.setState({ creditBriefLoading: true });
        generateCreditBrief(this.props.caseId, regenerate)
            .then(res => {
                const url =
                    res && res.data && res.data.data && res.data.data.url;
                if (url) {
                    // The PDF lives at a fixed S3 key (stable URL), so the
                    // browser would serve a stale cached copy after a
                    // regenerate. Append a cache-bust param so the iframe
                    // always loads the freshly stored file. A presigned S3
                    // URL is already unique per request, and any extra query
                    // param breaks its signature (403 SignatureDoesNotMatch).
                    const bust = url.indexOf("X-Amz-Signature=") !== -1
                        ? ""
                        : (url.indexOf("?") === -1 ? "?" : "&") +
                          "t=" + Date.now();
                    this.setState({ creditBriefUrl: url + bust });
                } else {
                    message.error("Credit brief not available", 3);
                }
            })
            .catch(() => message.error("Failed to generate credit brief", 3))
            .finally(() => this.setState({ creditBriefLoading: false }));
    };

    // The "Regenerate PDF" button forces a fresh render + store.
    handleDownloadCreditBrief = () => this.loadCreditBrief(true);

    componentWillUnmount() {
        if (this._printTimer) {
            clearTimeout(this._printTimer);
        }
        if (this.observer) {
            this.observer.disconnect();
        }
        window.removeEventListener("afterprint", this.handleAfterPrint);
    }

    // Kick off the redux fetches the tab depends on, only when not already loaded.
    // An applicant's permanent address was saved. Addresses hang off the full
    // company profile, so re-read it — the GET is the source of truth for the
    // Applicant Details table.
    handleAddressSaved = () => {
        const { caseId, companyId } = this.props;
        if (companyId) {
            this.props.fetchCompanyProfile(companyId, caseId);
        }
    };

    ensureData() {
        const { caseId, companyId, company } = this.props;
        // The company can be present in redux but cached *slim* (from a
        // /company/list/ call) without its addresses. Addresses are only
        // returned by the full /company/{id}/ profile endpoint, and both the
        // residence addresses (Applicant Details) and the registered/operating
        // addresses (Company Detail) live in that list. So fetch the full
        // profile whenever the address list is missing, not just when the
        // company record is absent.
        const addresses = company && company.get && company.get("addresses");
        const needsProfile = !company || !(addresses && addresses.size);
        if (companyId && needsProfile && !this._profileRequested) {
            this._profileRequested = true;
            this.props.fetchCompanyProfile(companyId, caseId);
        }
        if (
            !this.props.documents ||
            this.props.documents.get("status") === STATE_STATUS.UNFETCHED
        ) {
            this.props.fetchCaseDocuments(caseId, companyId, true);
        }
        if (
            companyId &&
            (!this.props.companyDocuments ||
                this.props.companyDocuments.get("status") ===
                    STATE_STATUS.UNFETCHED)
        ) {
            this.props.fetchCompanyDocuments(caseId, companyId);
        }
        // CAM engine parameter checks (read-only GET) — fetch once.
        if (!this.props.camEngineData && !this._camRequested) {
            this._camRequested = true;
            this.props.fetchCamEngineView(caseId);
        }
        if (this.props.supplyChainStatus === STATE_STATUS.UNFETCHED) {
            this.props.fetchCaseSupplyChainDetails(caseId);
        }
        if (this.props.financialsStatus === STATE_STATUS.UNFETCHED) {
            this.props.fetchCaseSupplyChainFinancials(caseId);
        }
        if (!this.props.caseAssociates) {
            this.props.fetchCaseAssociates(caseId);
        }
        // Proposed limit is edited through the caseDetailFields slice (same
        // contract as the Credit Appraisal tab); fetch it if not already loaded
        // so the Limit Assessment edit control has a value to show.
        if (this.props.caseDetailFieldsStatus === STATE_STATUS.UNFETCHED) {
            this.props.fetchCaseDetailFields(caseId);
        }
        if (this.props.activeBankAccountKey && !this._statementRequested) {
            this._statementRequested = true;
            this.props.fetchStatement(caseId, this.props.activeBankAccountKey);
        }
    }

    fetchCibilDocs() {
        CompanyService.getCibilDocuments(this.props.caseId)
            .then(response => {
                this.setState({
                    cibilDocs: (response.data && response.data.results) || [],
                    cibilDocsLoading: false
                });
            })
            .catch(() => {
                this.setState({ cibilDocs: [], cibilDocsLoading: false });
            });
    }

    // Real bureau records: report PDF url + pulled date per applicant.
    fetchBureauRecords() {
        CaseService.fetchCaseCibilAllApplicants(this.props.caseId)
            .then(response => {
                const data = response.data;
                this.setState({
                    bureauRecords: Array.isArray(data)
                        ? data
                        : (data && data.results) || (data && data.data) || []
                });
            })
            .catch(() => this.setState({ bureauRecords: [] }));
    }

    // Highlights the section currently dominating the scroll viewport.
    setupScrollSpy() {
        if (typeof IntersectionObserver === "undefined") {
            return;
        }
        this.observer = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    this.intersectionRatios[entry.target.id] = entry.isIntersecting
                        ? entry.intersectionRatio
                        : 0;
                });
                const active = Object.keys(this.intersectionRatios).reduce(
                    (best, id) =>
                        this.intersectionRatios[id] >
                        (this.intersectionRatios[best] || 0)
                            ? id
                            : best,
                    this.state.activeSection
                );
                if (active && active !== this.state.activeSection) {
                    this.setState({ activeSection: active });
                }
            },
            {
                root: this.contentRef.current || null,
                threshold: [0.1, 0.25, 0.5, 0.75]
            }
        );
        CREDIT_CHECKER_SECTIONS.forEach(section => {
            const node = this.sectionRefs[section.id].current;
            if (node) {
                this.observer.observe(node);
            }
        });
    }

    // Expand every section so all data is in the DOM, then open the browser
    // print dialog (Save as PDF) — the print stylesheet lays it out cleanly.
    handlePrint = () => {
        // Expand everything AND drop into "printing" mode (which unmounts the
        // heavy recommendation PDF iframe) in a single render, then open the
        // dialog once that lightweight DOM has painted.
        const sections = CREDIT_CHECKER_SECTIONS.reduce((acc, section) => {
            acc[section.id] = true;
            return acc;
        }, {});
        this.setState({ printing: true, sections }, () => {
            if (this._printTimer) {
                clearTimeout(this._printTimer);
            }
            this._printTimer = setTimeout(() => window.print(), 250);
        });
    };

    handleNavigate = sectionId => {
        const node = this.sectionRefs[sectionId].current;
        const container = this.contentRef.current;
        if (node && container) {
            // Offset by the sticky toolbar height so the section header isn't
            // hidden beneath it after scrolling.
            const toolbar = container.querySelector(".credit-checker__toolbar");
            const offset = (toolbar ? toolbar.offsetHeight : 0) + 12;
            const top =
                node.getBoundingClientRect().top -
                container.getBoundingClientRect().top +
                container.scrollTop -
                offset;
            container.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
        } else if (node && node.scrollIntoView) {
            node.scrollIntoView({ behavior: "smooth", block: "start" });
        }
        this.setState({ activeSection: sectionId });
    };

    render() {
        const mock = getCreditCheckerMock(this.props.caseId);
        // Triangulation: prefer the real API-built view-model (works for any
        // case where the API returns overlapping months); fall back to the
        // per-case mock baseline (only populated for 158262) so the demo case
        // keeps rendering even when the API has nothing.
        const apiTriangulation = buildTriangulationFromApi(
            this.state.dataComparison,
            { supplyChainRecord: this.props.supplyChainRecord }
        );
        const mockTriangulation =
            mock && mock.triangulation
                ? buildTriangulation({
                    mock: mock.triangulation,
                    financials: this.props.financials,
                    repaymentData: this.state.repaymentData,
                    statements: this.props.statements,
                    bankAccountKey: this.props.activeBankAccountKey,
                    supplyChainRecord: this.props.supplyChainRecord,
                    displayType:
                        this.props.caseDetail &&
                        (this.props.caseDetail.get
                            ? this.props.caseDetail.get(
                                "sales_analysis_display_type"
                            )
                            : this.props.caseDetail
                                .sales_analysis_display_type)
                })
                : null;
        const triangulation = apiTriangulation || mockTriangulation;
        const applicants = buildApplicants({
            caseAssociates: this.props.caseAssociates,
            company: this.props.company,
            documents: this.props.documents,
            companyDocuments: this.props.companyDocuments,
            cibilDocs: this.state.cibilDocs,
            bureauRecords: this.state.bureauRecords
        });
        const applicantsLoading =
            this.state.cibilDocsLoading || !this.props.caseAssociates;
        // ASM contact number for the TVR call picker.
        const callContacts = buildCallContacts({
            caseDetail: this.props.caseDetail
        });
        const company = buildCompany({
            company: this.props.company,
            documents: this.props.documents,
            companyDocuments: this.props.companyDocuments,
            caseDetail: this.props.caseDetail,
            fetchFilingRecord:
                this.props.caseDetailFields &&
                this.props.caseDetailFields.get("fetch_filing_record")
        });
        const companyLoading =
            !this.props.company ||
            this.props.companyStatus === STATE_STATUS.FETCHING;
        const triangulationDocs = buildTriangulationDocs({
            documents: this.props.documents,
            companyDocuments: this.props.companyDocuments
        });
        const cam = buildCamAssessment(this.props.camEngineData);
        // Overlay any in-flight credit-remark edits so the editable inputs show
        // the current draft (buildCamAssessment returns a fresh object, safe to
        // mutate).
        const camEdits = this.state.camRemarkEdits || {};
        if (cam && cam.categories && Object.keys(camEdits).length) {
            cam.categories.forEach(catg => {
                (catg.rows || []).forEach(row => {
                    if (row.key2 in camEdits) {
                        row.creditRemarks = camEdits[row.key2];
                    }
                });
            });
        }
        const camLoading =
            !this.props.camEngineData &&
            this.props.camEngineStatus === STATE_STATUS.FETCHING;
        // Real headline metrics from the supply-chain record + case detail,
        // plus the GST monthly comparison (used for the GST YTD/FY topline
        // tiles that mirror the PDF's Limit Assessment block).
        const topMetrics = buildTopMetrics({
            supplyChainRecord: this.props.supplyChainRecord,
            caseDetail: this.props.caseDetail,
            dataComparison: this.state.dataComparison
        });
        // Trackwizz — parsed once per render; the KYC row + Company Screening
        // card both read from this without redoing the Python-repr parse.
        const applicantHits = parseApplicantHits(this.state.trackwizzData);
        const companyScreening = parseCompanyScreening(this.state.trackwizzData);
        // Pricing — ROI / Tenure / Subvention / Margin / SD from the scheme.
        const pricing = buildPricing({ caseDetail: this.props.caseDetail });

        // Eligibility Calculator gating — copied verbatim from the Credit
        // Appraisal tab: bank ∈ {KSF|HHPL, HHPL} AND scheme is a Term Loan.
        // caseDetail may be a plain object or an Immutable record, so read via
        // an accessor that works for both. `isKSFTl` switches the calculator into
        // its KSF term-loan mode (same computation as the appraisal tab).
        const _cd = this.props.caseDetail;
        const _cdGet = key =>
            _cd ? (_cd.get ? _cd.get(key) : _cd[key]) : null;
        const _bank = _cdGet("bank");
        const _bankCode = _bank
            ? _bank.get
                ? _bank.get("code")
                : _bank.code
            : null;
        const _isTermLoan = String(_cdGet("scheme_type") || "") === "Term Loan";
        const showEligibilityCalculator =
            Boolean(_bank) &&
            ["KSF|HHPL", "HHPL"].includes(_bankCode) &&
            _isTermLoan;
        const isKSFTl = _bankCode === "KSF|HHPL" && _isTermLoan;
        // Credit Brief is composed + rendered on demand (handleDownloadCreditBrief)
        // from these same view-models, so nothing is built here in render().
        // Recommendation section disabled for now.
        // const recommendation = buildRecommendation({
        //     caseDetail: this.props.caseDetail,
        //     company,
        //     supplyChainRecord: this.props.supplyChainRecord,
        //     documents: this.props.documents,
        //     companyDocuments: this.props.companyDocuments
        // });
        const userRecord = this.props.user;
        const currentUser = userRecord
            ? {
                id: userRecord.get ? userRecord.get("id") : userRecord.id,
                name:
                    userRecord.full_name ||
                    (userRecord.get && userRecord.get("email")) ||
                    "User"
            }
            : { id: null, name: "User" };
        const allExpanded = CREDIT_CHECKER_SECTIONS.every(
            section => this.state.sections[section.id]
        );

        return (
            <div className={"credit-checker"}>
                <CreditCheckerNav
                    activeSection={this.state.activeSection}
                    onNavigate={this.handleNavigate}
                    hiddenSections={
                        hasProductDetails(this.props.caseDetail)
                            ? undefined
                            : [SECTION_IDS.PRODUCT_DETAILS]
                    }
                />
                <div className={"credit-checker__content"} ref={this.contentRef}>
                    <div className={"credit-checker__toolbar"}>
                        <button
                            type={"button"}
                            className={"cc-toggle-all"}
                            onClick={() => this.setAllSections(!allExpanded)}
                        >
                            <Icon type={allExpanded ? "shrink" : "arrows-alt"} />
                            {allExpanded ? "Collapse all" : "Expand all"}
                        </button>
                    </div>
                    <div className={"cc-print-header"}>
                        <div className={"cc-print-header__title"}>
                            Credit Checker — Underwriting Summary
                        </div>
                        <div className={"cc-print-header__sub"}>
                            {(company && company.name) || "Company"} · Case PGCP-
                            {this.props.caseId}
                        </div>
                    </div>
                    <CompanyDetailSection
                        innerRef={this.sectionRefs[SECTION_IDS.COMPANY_DETAIL]}
                        caseId={this.props.caseId}
                        companyId={this.props.companyId}
                        userGroups={this.props.userGroups}
                        user={this.props.user}
                        fetchCaseDetail={this.props.fetchCaseDetail}
                        onAddressSaved={this.handleAddressSaved}
                        company={company}
                        companyScreening={companyScreening}
                        dependence={
                            this.props.caseDetailFields &&
                            this.props.caseDetailFields.get("dependence")
                        }
                        brandMix={this.state.brandMix}
                        brandMixLoading={this.state.brandMixLoading}
                        brandMixCount={this.state.brandMixCount}
                        brandMixPage={this.state.brandMixPage}
                        brandMixPageSize={BRAND_MIX_PAGE_SIZE}
                        brandMixSearch={this.state.brandMixSearch}
                        onBrandMixPageChange={this.setBrandMixPage}
                        onBrandMixSearch={this.setBrandMixSearch}
                        onBrandMixAdded={() => this.fetchBrandMix(1)}
                        onAsmUpdated={() =>
                            this.props.fetchCaseDetail &&
                            this.props.fetchCaseDetail(this.props.caseId)
                        }
                        stageId={this.props.stageId}
                        isUserAllowedToEdit={this.props.isUserAllowedToEdit}
                        loading={companyLoading}
                        expanded={this.state.sections[SECTION_IDS.COMPANY_DETAIL]}
                        onToggle={() =>
                            this.toggleSection(SECTION_IDS.COMPANY_DETAIL)
                        }
                    />
                    <ExistingPortfolioSection
                        innerRef={
                            this.sectionRefs[SECTION_IDS.EXISTING_PORTFOLIO]
                        }
                        caseId={this.props.caseId}
                        portfolioView={this.getPortfolioViewWithComments()}
                        portfolioLoading={
                            this.props.portfolioViewStatus ===
                            STATE_STATUS.FETCHING
                        }
                        stageId={this.props.stageId}
                        onOpenPortfolioRemarks={this.openPortfolioRemarksDrawer}
                        limitInfo={this.state.limitInfo}
                        allCases={this.state.allCases}
                        limitLoading={this.state.limitLoading}
                        onRebuildLimitInfo={this.handleRebuildLimitInfo}
                        onGroupInfoRebuilt={this.handleRefreshExposureData}
                        onGroupInfoValidationChange={
                            this.props.setGroupInfoAllValidated
                        }
                        onFiMovementDone={() => this.fetchExistingPortfolio()}
                        permissionContext={this.props.permissionContext}
                        recreditViewOnlyPermission={
                            this.props.recreditViewOnlyPermission
                        }
                        casesLoading={this.state.casesLoading}
                        expanded={
                            this.state.sections[SECTION_IDS.EXISTING_PORTFOLIO]
                        }
                        onToggle={() =>
                            this.toggleSection(SECTION_IDS.EXISTING_PORTFOLIO)
                        }
                    />
                    <BrandMixCard
                        caseId={this.props.caseId}
                        stageId={this.props.stageId}
                        brandMix={this.state.brandMix}
                        brandMixLoading={this.state.brandMixLoading}
                        brandMixCount={this.state.brandMixCount}
                        brandMixPage={this.state.brandMixPage}
                        brandMixPageSize={BRAND_MIX_PAGE_SIZE}
                        brandMixSearch={this.state.brandMixSearch}
                        onBrandMixPageChange={this.setBrandMixPage}
                        onBrandMixSearch={this.setBrandMixSearch}
                        onBrandMixAdded={() => this.fetchBrandMix(1)}
                        dependence={
                            this.props.caseDetailFields &&
                            this.props.caseDetailFields.get("dependence")
                        }
                    />
                    <ApplicantDetailsSection
                        innerRef={
                            this.sectionRefs[SECTION_IDS.APPLICANT_DETAILS]
                        }
                        caseId={this.props.caseId}
                        stageId={this.props.stageId}
                        companyId={this.props.companyId}
                        userGroups={this.props.userGroups}
                        onAddressSaved={this.handleAddressSaved}
                        onChangeRelationship={this.handleChangeRelationship}
                        savingRelationshipId={this.state.savingRelationshipId}
                        onUpdateShareholding={this.handleUpdateShareholding}
                        canEditShareholding={true}
                        savingShareholdingId={this.state.savingShareholdingId}
                        onUpdateEmail={this.handleUpdateEmail}
                        savingEmailId={this.state.savingEmailId}
                        applicants={applicants}
                        applicantHits={applicantHits}
                        loading={applicantsLoading}
                        onSaveBureauRemark={this.handleSaveBureauRemark}
                        savingRemarkId={this.state.savingRemarkId}
                        onToggleMainBorrower={this.handleToggleMainBorrower}
                        savingMainBorrowerId={this.state.savingMainBorrowerId}
                        onAddressChanged={() => {
                            // Refresh both slices so the KYC table re-derives
                            // residence/permanent from the latest addresses.
                            if (this.props.companyId) {
                                this.props.fetchCompanyProfile(
                                    this.props.companyId,
                                    this.props.caseId
                                );
                            }
                            this.props.fetchCaseAssociates(
                                this.props.caseId
                            );
                        }}
                        expanded={
                            this.state.sections[SECTION_IDS.APPLICANT_DETAILS]
                        }
                        onToggle={() =>
                            this.toggleSection(SECTION_IDS.APPLICANT_DETAILS)
                        }
                    />
                    <ProductDetailsSection
                        innerRef={this.sectionRefs[SECTION_IDS.PRODUCT_DETAILS]}
                        caseId={this.props.caseId}
                        caseDetail={this.props.caseDetail}
                        recreditViewOnlyPermission={
                            this.props.recreditViewOnlyPermission
                        }
                        expanded={
                            this.state.sections[SECTION_IDS.PRODUCT_DETAILS]
                        }
                        onToggle={() =>
                            this.toggleSection(SECTION_IDS.PRODUCT_DETAILS)
                        }
                    />
                    <CaseSummarySection
                        innerRef={this.sectionRefs[SECTION_IDS.CASE_SUMMARY]}
                        caseId={this.props.caseId}
                        topMetrics={topMetrics}
                        stageId={this.props.stageId}
                        proposedLimit={
                            this.props.caseDetailFields &&
                            this.props.caseDetailFields.get("proposed_limit")
                        }
                        salesProposedLimit={_cdGet("sales_proposed_limit")}
                        isRiskApprover={this.props.isRiskApprover}
                        isUserAllowedToEdit={this.props.isUserAllowedToEdit}
                        recreditViewOnlyPermission={
                            this.props.recreditViewOnlyPermission
                        }
                        onProposedLimitChange={this.onProposedLimitChange}
                        onSalesProposedLimitChange={
                            this.onSalesProposedLimitChange
                        }
                        exposureData={_cdGet("exposure_data")}
                        exposureRefreshing={this.state.exposureRefreshing}
                        onRefreshExposure={this.handleRefreshExposureData}
                        expanded={this.state.sections[SECTION_IDS.CASE_SUMMARY]}
                        onToggle={() =>
                            this.toggleSection(SECTION_IDS.CASE_SUMMARY)
                        }
                    />
                    {/* Eligibility Calculator — collapsed-by-default accordion
                        placed directly below Limit Assessment (Term Loan cases
                        only; same KSF/HHPL gate as Credit Appraisal). Moved out
                        of the Current Loan Obligation section per UI ask. */}
                    {showEligibilityCalculator ? (
                        <CardBlock
                            icon={"calculator"}
                            title={"Eligibility Calculator"}
                            className={"cc-eligibility"}
                            collapsible
                            defaultCollapsed
                        >
                            <EligibilityCalculator
                                calculatorData={this.state.calculatorData}
                                financialsData={
                                    this.props.financials &&
                                    this.props.financials.get &&
                                    this.props.financials.get("data")
                                }
                                caseId={this.props.caseId}
                                isKSFTl={isKSFTl}
                                bankStatementAnalyser={
                                    this.props.bankStatementAnalyser
                                }
                                caseDetail={this.props.caseDetail}
                            />
                        </CardBlock>
                    ) : null}
                    {/* Existing Relationship + Brand Mix moved earlier
                        (right after Company Detail) per 2026-07-22 ask so
                        the borrower's existing Progfin exposure + brand mix
                        read immediately after the identity block, before
                        Applicants / Limit Assessment. */}
                    <BureauLoanSection
                        innerRef={this.sectionRefs[SECTION_IDS.BUREAU_LOAN]}
                        caseId={this.props.caseId}
                        stageId={this.props.stageId}
                        data={this.state.bureauLoans}
                        summary={this.state.bureauSummary}
                        loading={this.state.bureauLoansLoading}
                        expanded={this.state.sections[SECTION_IDS.BUREAU_LOAN]}
                        onToggle={() =>
                            this.toggleSection(SECTION_IDS.BUREAU_LOAN)
                        }
                        onRefresh={() => this.fetchBureauLoans()}
                        onTriggerCibil={this.handleTriggerCibil}
                        triggeringCibilId={this.state.triggeringCibilId}
                        loanFilter={this.state.bureauLoanFilter}
                        onLoanFilterChange={this.setBureauLoanFilter}
                        includeClosed={this.state.bureauIncludeClosed}
                        onIncludeClosedChange={this.setBureauIncludeClosed}
                    />
                    <TriangulationSection
                        innerRef={this.sectionRefs[SECTION_IDS.TRIANGULATION]}
                        caseId={this.props.caseId}
                        companyId={this.props.companyId}
                        bankCode={this.props.bankCode || ""}
                        company={this.props.company}
                        triangulation={triangulation}
                        triangulationDocs={triangulationDocs}
                        expanded={this.state.sections[SECTION_IDS.TRIANGULATION]}
                        onToggle={() =>
                            this.toggleSection(SECTION_IDS.TRIANGULATION)
                        }
                        statements={this.props.statements}
                        fetchStatement={this.props.fetchStatement}
                        createStatement={this.props.createStatement}
                        updateStatement={this.props.updateStatement}
                        deleteStatement={this.props.deleteStatement}
                        updateCaseDetail={this.props.updateCaseDetail}
                        updateCaseDetailFields={this.props.updateCaseDetailFields}
                        use_statement_analyzer={this.props.use_statement_analyzer}
                        bankStatementAnalyser={this.props.bankStatementAnalyser}
                        createRequest={this.props.createRequest}
                        createProfiosRequest={this.props.createProfiosRequest}
                        fetchJobsList={this.props.fetchJobsList}
                        fetchJobsDetails={this.props.fetchJobsDetails}
                        fetchJobsDetailsPerfios={this.props.fetchJobsDetailsPerfios}
                        fetchStatementAnalysis={this.props.fetchStatementAnalysis}
                        fetchStatementAnalysisPerfois={
                            this.props.fetchStatementAnalysisPerfois
                        }
                        retryAnalysis={this.props.retryAnalysis}
                        retryAnalysisPerfios={this.props.retryAnalysisPerfios}
                        adminGroup={this.props.adminGroup}
                        userGroup={this.props.user && this.props.user.get("groups")}
                        stageId={this.props.stageId}
                        lendingPartner={this.props.lendingPartner}
                        caseDetail={this.props.caseDetail}
                        isRiskApprover={this.props.isRiskApprover}
                        caseBankAccounts={this.props.caseBankAccounts}
                        caseBankAccountsStatus={
                            this.props.caseBankAccountsStatus
                        }
                        fetchCaseBankAccounts={this.props.fetchCaseBankAccounts}
                        addCaseBankAccounts={this.props.addCaseBankAccounts}
                        updateCompanyBankAccounts={
                            this.props.updateCompanyBankAccounts
                        }
                    />
                    {/* Inline Financial Analysis — full tab (actions + view)
                        shown for every case. Section defaults collapsed (see
                        constants: defaultCollapsed) so its bulk doesn't
                        dominate the scroll on load. */}
                    {(() => {
                        return (
                            <SectionBlock
                                id={SECTION_IDS.FINANCIAL_ANALYSIS}
                                innerRef={
                                    this.sectionRefs[
                                        SECTION_IDS.FINANCIAL_ANALYSIS
                                    ]
                                }
                                title={"Financial Analysis"}
                                icon={"line-chart"}
                                expanded={
                                    this.state.sections[
                                        SECTION_IDS.FINANCIAL_ANALYSIS
                                    ]
                                }
                                onToggle={() =>
                                    this.toggleSection(
                                        SECTION_IDS.FINANCIAL_ANALYSIS
                                    )
                                }
                            >
                                <FinancialAnalysisTab
                                    companyId={this.props.companyId}
                                    caseId={this.props.caseId}
                                    caseDetail={this.props.caseDetail}
                                    isRiskApprover={this.props.isRiskApprover}
                                    isUserAllowedToEdit={
                                        this.props.isUserAllowedToEdit
                                    }
                                    /* Save (per table) and the Excel upload
                                       are only actionable at the credit
                                       stages — same gate used elsewhere in
                                       Credit Checker. Reuses the existing
                                       recreditViewOnlyPermission prop this
                                       shared component already forwards to
                                       its Save buttons, so other tabs/the
                                       company drawer that don't pass this
                                       prop are unaffected. */
                                    recreditViewOnlyPermission={
                                        ![7, 8].includes(
                                            Number(this.props.stageId)
                                        )
                                    }
                                />
                            </SectionBlock>
                        );
                    })()}
                    <CamAssessmentSection
                        innerRef={this.sectionRefs[SECTION_IDS.CAM_ASSESSMENT]}
                        cam={cam}
                        camType={
                            this.props.caseDetail &&
                            this.props.caseDetail.cam_type
                        }
                        loading={camLoading}
                        onRefresh={this.handleRefreshCam}
                        refreshing={this.state.camRefreshing}
                        stageId={this.props.stageId}
                        onEditRemark={this.onEditCamRemark}
                        onUpdate={this.handleUpdateCam}
                        updating={this.state.camUpdating}
                        expanded={
                            this.state.sections[SECTION_IDS.CAM_ASSESSMENT]
                        }
                        onToggle={() =>
                            this.toggleSection(SECTION_IDS.CAM_ASSESSMENT)
                        }
                    />
                    <UpdatedTvrSection
                        innerRef={this.sectionRefs[SECTION_IDS.UPDATED_TVR]}
                        caseId={this.props.caseId}
                        stageId={this.props.stageId}
                        onTvrSaved={tvr =>
                            this.props.fetchedCaseTvr(this.props.caseId, tvr)
                        }
                        onAiRefreshed={this.handleAiRefreshed}
                        user={currentUser}
                        call={mock && mock.call}
                        applicants={applicants}
                        contacts={callContacts}
                        /* Limit Assessment — SAME props the standalone Limit
                           Assessment section gets, so both render one shared
                           card over one source of truth. */
                        topMetrics={topMetrics}
                        proposedLimit={
                            this.props.caseDetailFields &&
                            this.props.caseDetailFields.get("proposed_limit")
                        }
                        exposureData={_cdGet("exposure_data")}
                        isRiskApprover={this.props.isRiskApprover}
                        isUserAllowedToEdit={this.props.isUserAllowedToEdit}
                        recreditViewOnlyPermission={
                            this.props.recreditViewOnlyPermission
                        }
                        onProposedLimitChange={this.onProposedLimitChange}
                        expanded={this.state.sections[SECTION_IDS.UPDATED_TVR]}
                        onToggle={() =>
                            this.toggleSection(SECTION_IDS.UPDATED_TVR)
                        }
                    />
                    {/* Case Summary — the SAME rich-text remarks editor (with
                        Save / Undo + stage/assignment save-gate) the Credit
                        Appraisal tab renders, placed directly below TVR.
                        COMMENTED OUT per ask — gated off with `false &&`. */}
                    {false && (
                    <SectionBlock
                        id={SECTION_IDS.CASE_SUMMARY_REMARKS}
                        innerRef={
                            this.sectionRefs[SECTION_IDS.CASE_SUMMARY_REMARKS]
                        }
                        title={"Case Summary"}
                        icon={"solution"}
                        expanded={
                            this.state.sections[
                                SECTION_IDS.CASE_SUMMARY_REMARKS
                            ]
                        }
                        onToggle={() =>
                            this.toggleSection(
                                SECTION_IDS.CASE_SUMMARY_REMARKS
                            )
                        }
                    >
                        <div className={"cc-case-summary"}>
                        <RemarksEditor
                            hideTitle
                            loading={
                                this.props.remarksStatus ===
                                STATE_STATUS.FETCHING
                            }
                            getCaseRemarks={this.getCaseRemarks}
                            remarks={
                                this.props.caseDetail &&
                                (this.props.caseDetail.get
                                    ? this.props.caseDetail.get("remarks")
                                    : this.props.caseDetail.remarks)
                            }
                            onEdit={this.onRemarksEdit}
                            isUserAllowedToEdit={this.props.isUserAllowedToEdit}
                            recreditViewOnlyPermission={
                                this.props.recreditViewOnlyPermission
                            }
                            stageId={this.props.stageId}
                            thisUser={this.props.thisUser}
                            currentStageUser={this.props.currentStageUser}
                        />
                        </div>
                    </SectionBlock>
                    )}
                    <ActionableSection
                        innerRef={this.sectionRefs[SECTION_IDS.ACTIONABLE]}
                        expanded={this.state.sections[SECTION_IDS.ACTIONABLE]}
                        onToggle={() =>
                            this.toggleSection(SECTION_IDS.ACTIONABLE)
                        }
                        caseId={this.props.caseId}
                        caseDetailFields={this.props.caseDetailFields}
                        caseDetailFieldsStatus={
                            this.props.caseDetailFieldsStatus
                        }
                        caseData={this.props.caseData}
                        caseDetail={this.props.caseDetail}
                        stageId={this.props.stageId}
                        userGroup={
                            this.props.user && this.props.user.get("groups")
                        }
                        isHighValueCase={this.props.isHighValueCase}
                        fetchCaseDetail={this.props.fetchCaseDetail}
                        fetchCaseDetailFields={this.props.fetchCaseDetailFields}
                        updateCaseDetail={this.props.updateCaseDetail}
                    />
                    <PricingSection
                        innerRef={this.sectionRefs[SECTION_IDS.PRICING]}
                        expanded={this.state.sections[SECTION_IDS.PRICING]}
                        onToggle={() =>
                            this.toggleSection(SECTION_IDS.PRICING)
                        }
                        pricing={pricing}
                        caseId={this.props.caseId}
                        companyId={this.props.companyId}
                        caseDetail={this.props.caseDetail}
                        stageId={this.props.stageId}
                        isUserAllowedToEdit={this.props.isUserAllowedToEdit}
                        recreditViewOnlyPermission={
                            this.props.recreditViewOnlyPermission
                        }
                        location={this.props.location}
                        companyName={this.props.companyName}
                        stageName={this.props.stageName}
                    />
                    <CreditDecisionSection
                        innerRef={this.sectionRefs[SECTION_IDS.CREDIT_DECISION]}
                        caseId={this.props.caseId}
                        stageId={this.props.stageId}
                        aiReloadKey={this.state.aiReloadKey}
                        expanded={this.state.sections[SECTION_IDS.CREDIT_DECISION]}
                        onToggle={() =>
                            this.toggleSection(SECTION_IDS.CREDIT_DECISION)
                        }
                        sanctionLimit={
                            (this.props.caseDetailFields &&
                                (this.props.caseDetailFields.get(
                                    "sanction_limit"
                                ) ||
                                    this.props.caseDetailFields.get(
                                        "proposed_limit"
                                    ))) ||
                            null
                        }
                        sanctionDate={
                            this.props.caseDetailFields &&
                            this.props.caseDetailFields.get("sanction_date")
                        }
                    />
                    <CreditBriefSection
                        innerRef={this.sectionRefs[SECTION_IDS.CREDIT_BRIEF]}
                        caseId={this.props.caseId}
                        stageId={this.props.stageId}
                        onDownloadPdf={this.handleDownloadCreditBrief}
                        pdfLoading={this.state.creditBriefLoading}
                        pdfUrl={this.state.creditBriefUrl}
                        expanded={this.state.sections[SECTION_IDS.CREDIT_BRIEF]}
                        onToggle={() =>
                            this.toggleSection(SECTION_IDS.CREDIT_BRIEF)
                        }
                    />
                    {/* Recommendation section disabled for now.
                    <RecommendationSection
                        innerRef={this.sectionRefs[SECTION_IDS.RECOMMENDATION]}
                        recommendation={recommendation}
                        printing={this.state.printing}
                        expanded={this.state.sections[SECTION_IDS.RECOMMENDATION]}
                        onToggle={() =>
                            this.toggleSection(SECTION_IDS.RECOMMENDATION)
                        }
                    />
                    */}
                </div>
                <PortfolioCreditRemarksDrawer
                    visible={this.state.portfolioRemarksDrawerVisible}
                    onClose={this.closePortfolioRemarksDrawer}
                    caseId={this.props.caseId}
                    stageId={this.props.stageId}
                    instanceId={
                        this.state.portfolioRemarksRecord &&
                        this.state.portfolioRemarksRecord.portfolioInstanceId
                    }
                    remarkType={
                        this.state.portfolioRemarksRecord &&
                        this.state.portfolioRemarksRecord.key
                    }
                    category={
                        this.state.portfolioRemarksRecord &&
                        this.state.portfolioRemarksRecord.category
                    }
                    onRemarkSaved={this.handlePortfolioRemarkSaved}
                />
            </div>
        );
    }
}

CreditChecker.propTypes = {
    caseId: PropTypes.number,
    companyId: PropTypes.number,
    caseDetail: PropTypes.object,
    fetchCaseDetail: PropTypes.func,
    fetchedCaseDetail: PropTypes.func,
    company: PropTypes.object,
    companyStatus: PropTypes.string,
    caseAssociates: PropTypes.object,
    documents: PropTypes.object,
    companyDocuments: PropTypes.object,
    camEngineData: PropTypes.object,
    camEngineStatus: PropTypes.string,
    fetchCamEngineView: PropTypes.func,
    addCaseAssociates: PropTypes.func,
    supplyChainStatus: PropTypes.string,
    supplyChainRecord: PropTypes.object,
    financials: PropTypes.object,
    financialsStatus: PropTypes.string,
    statements: PropTypes.object,
    activeBankAccountKey: PropTypes.number,
    fetchCompanyProfile: PropTypes.func,
    fetchCaseDocuments: PropTypes.func,
    fetchCompanyDocuments: PropTypes.func,
    fetchCaseAssociates: PropTypes.func,
    fetchCaseSupplyChainDetails: PropTypes.func,
    fetchCaseSupplyChainFinancials: PropTypes.func,
    fetchStatement: PropTypes.func,
    fetchPortfolioView: PropTypes.func,
    portfolioView: PropTypes.array,
    portfolioViewStatus: PropTypes.string,
    user: PropTypes.object
};

const mapStateToProps = (state, props) => ({
    user: state.user.getIn(["data"]),
    // Case Summary (remarks) wiring — same slices the Credit Appraisal tab reads.
    thisUser: state.user.getIn(["data", "id"]),
    currentStageUser: state.caseDetails.getIn([props.caseId, "data", "current_case_task"]),
    caseInternalRemarks: state.caseDetails.getIn([props.caseId, "data", "internal_remarks"]),
    remarksStatus: state.caseDetails.getIn([props.caseId, "remarksStatus"], STATE_STATUS.UNFETCHED),
    company: state.companies.getIn([props.companyId, "data"]),
    companyStatus: state.companies.getIn([
        props.companyId,
        "status",
        "companyStatus"
    ]),
    // Bank Accounts card: the case↔account applicability map
    // ("Select Bank Account Applicable To Case").
    caseBankAccountsStatus: state.caseDetails.getIn([
        Number(props.caseId),
        "caseBankAccounts",
        "status"
    ]),
    caseBankAccounts: state.caseDetails.getIn([
        props.caseId,
        "caseBankAccounts",
        "data"
    ]),
    caseAssociates: state.caseDetails.getIn([
        props.caseId,
        "caseAssociates",
        "data"
    ]),
    documents: state.documents.getIn(["case_documents", props.caseId]),
    companyDocuments: state.documents.getIn([
        "company_documents",
        props.companyId
    ]),
    camEngineData: state.companyLosProfiles.getIn([
        "camEngineData",
        Number(props.caseId)
    ]),
    camEngineStatus: state.companyLosProfiles.get("camEngineStatus"),
    portfolioView: state.companyLosProfiles.getIn([
        "portfolioViewData",
        Number(props.caseId)
    ]),
    portfolioViewStatus: state.companyLosProfiles.get("portfolioViewStatus"),
    supplyChainRecord: state.supplyChain.getIn(["records", props.caseId, "data"]),
    supplyChainStatus: state.supplyChain.getIn([
        "records",
        props.caseId,
        "status"
    ]),
    financials: state.supplyChain.getIn(["financials", props.caseId, "data"]),
    financialsStatus: state.supplyChain.getIn([
        "financials",
        props.caseId,
        "status"
    ]),
    statements: state.statements.get(props.caseId),
    // Editable proposed limit (Limit Assessment) — same source the Credit
    // Appraisal tab uses for CreditAppraisalProposedLimit.
    caseDetailFields: state.caseDetails.getIn([
        props.caseId,
        "caseDetailFields",
        "data"
    ]),
    caseDetailFieldsStatus: state.caseDetails.getIn(
        [props.caseId, "caseDetailFields", "status"],
        STATE_STATUS.UNFETCHED
    ),
    activeBankAccountKey: (
        (state.companies.getIn([props.companyId, "data", "bank_accounts"]) &&
            state.companies
                .getIn([props.companyId, "data", "bank_accounts"])
                .filter(_ => _.is_key_account)
                .first()) ||
        {}
    ).id,
    // Bank Statement Jobs (Credit Appraisal parity) — same slices the appraisal
    // tab reads so the Jobs List + Manual/Upload block stays in sync.
    bankStatementAnalyser: state.bankStatementAnalyser.get(props.caseId),
    use_statement_analyzer: state.caseDetails.getIn([
        props.caseId,
        "data",
        "use_statement_analyzer"
    ]),
    adminGroup: state.user.getIn(["data", "stage_admin"]),
    bankCode: state.caseDetails.getIn([props.caseId, "data", "bank", "code"]),
    // Same source the Credit Appraisal tab uses — the Bank Statement Analysis
    // upload form needs it.
    lendingPartner: state.caseDetails.getIn([props.caseId, "data", "bank", "id"]),
    // Full caseDetail.data (json_data lives here — the Actionable section
    // reads T&C and Growth Plan off it).
    caseData: state.caseDetails.getIn([props.caseId, "data"]),
    isHighValueCase: state.caseDetails.getIn([
        props.caseId,
        "data",
        "is_high_value_case"
    ])
});

export default connect(
    mapStateToProps,
    dispatch =>
        bindActionCreators(
            {
                fetchCompanyProfile,
                fetchCaseDocuments,
                fetchCompanyDocuments,
                fetchCaseAssociates,
                fetchCaseSupplyChainDetails,
                fetchCaseSupplyChainFinancials,
                fetchStatement,
                fetchCamEngineView,
                fetchPortfolioView,
                addCaseAssociates,
                updateCaseDetailFields,
                fetchCaseDetailFields,
                fetchedCaseTvr,
                setGroupInfoAllValidated,
                // Bank statement analyser actions
                createStatement,
                updateStatement,
                deleteStatement,
                updateCaseDetail,
                fetchCaseDetail,
                fetchedCaseDetail,
                updateCaseRemarks,
                fetchCaseRemarks,
                createRequest,
                createProfiosRequest,
                fetchJobsList,
                fetchJobsDetails,
                fetchJobsDetailsPerfios,
                fetchStatementAnalysis,
                fetchStatementAnalysisPerfois,
                retryAnalysis,
                retryAnalysisPerfios,
                // Bank Accounts actions
                updateCompanyBankAccounts,
                fetchCaseBankAccounts,
                addCaseBankAccounts
            },
            dispatch
        )
)(CreditChecker);
